import React, { useEffect, useState } from "react";
import { Search, Eye, X } from "lucide-react";
import axios from "axios";

import AdminLayout from "../components/AdminLayout";
import { BASE_URL } from "../../../config/config";
import { useNavigate } from "react-router-dom";

const PropertyOwners = () => {
  const [owners, setOwners] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate()

  // Property modal states
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [propertyError, setPropertyError] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerProperties, setOwnerProperties] = useState([]);

  // Get all owners
  const getOwners = async () => {
    setIsLoading(true);

    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.get(`${BASE_URL}/admin/owners`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Owners API response:", response);

      if (response?.data) {
        setOwners(response.data);
      }
    } catch (error) {
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      console.log("Error fetching owners:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOwners();
  }, []);

  // Get properties for selected owner
  const getOwnerProperties = async (owner) => {
    setSelectedOwner(owner);
    setIsPropertyModalOpen(true);
    setPropertyLoading(true);
    setPropertyError("");
    setOwnerProperties([]);

    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.get(
        `${BASE_URL}/admin/owners/${owner.id}/properties`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Owner properties API response:", response);

      if (response?.data) {
        setOwnerProperties(response.data);
      }
    } catch (error) {
      console.log("Error fetching owner properties:", error);

      setPropertyError(
        error?.response?.data?.message || "Unable to load owner properties.",
      );
    } finally {
      setPropertyLoading(false);
    }
  };

  const closePropertyModal = () => {
    setIsPropertyModalOpen(false);
    setSelectedOwner(null);
    setOwnerProperties([]);
    setPropertyError("");
  };

  const filteredOwners = owners.filter((owner) => {
    const fullName = `${owner.first_name || ""} ${
      owner.last_name || ""
    }`.trim();

    const searchValue = search.toLowerCase();

    return (
      fullName.toLowerCase().includes(searchValue) ||
      owner.email?.toLowerCase().includes(searchValue) ||
      owner.phone_number?.toLowerCase().includes(searchValue)
    );
  });

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSubmissionStatus = (status) => {
    if (!status) return "Pending";

    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus.includes("approved")) {
      return "Approved";
    }

    if (normalizedStatus.includes("reject")) {
      return "Rejected";
    }

    if (normalizedStatus.includes("pending")) {
      return "Pending";
    }

    return status;
  };

  return (
    <AdminLayout title="Property Owners">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-[17px] font-semibold text-[#142b3b]">
            Property Owners
          </h1>

          <p className="mt-1 text-[9px] text-[#84909b]">
            Manage landlords, developers, and their listings.
          </p>
        </div>

        <span className="rounded-[6px] bg-[#d9f8e4] px-3 py-2 text-[11px] font-medium text-[#159447]">
          {owners.length} Owners
        </span>
      </div>

      <div className="rounded-[8px] border border-[#e0e4e8] bg-white">
        {/* Search */}
        <div className="border-b border-[#edf0f2] p-2">
          <div className="relative max-w-[255px]">
            <Search
              size={12}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa4ad]"
            />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search property owners..."
              className="h-[30px] w-full rounded-[6px] border border-[#e0e4e8] pl-8 text-[10px] outline-none focus:border-[#8caefb]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-[#e7eaed] bg-[#fafbfc]">
                {[
                  "Owner",
                  "Phone Number",
                  "Email Verification",
                  "Created At",
                  "Action",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-3 py-2.5 text-left text-[10px] font-medium text-[#71808c]"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-[10px] text-[#84909b]"
                  >
                    Loading property owners...
                  </td>
                </tr>
              ) : filteredOwners.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-[10px] text-[#84909b]"
                  >
                    No property owners found
                  </td>
                </tr>
              ) : (
                filteredOwners.map((owner) => {
                  const fullName =
                    `${owner.first_name || ""} ${
                      owner.last_name || ""
                    }`.trim() || "Unnamed Owner";

                  const initials = `${owner.first_name?.[0] || ""}${
                    owner.last_name?.[0] || ""
                  }`.toUpperCase();

                  return (
                    <tr
                      key={owner.id}
                      className="border-b border-[#edf0f2] last:border-0"
                    >
                      {/* Owner */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-[#dce9ff] text-[9px] font-medium text-[#285ce7]">
                            {initials || "OW"}
                          </div>

                          <div>
                            <p className="text-[10px] font-medium text-[#243846]">
                              {fullName}
                            </p>

                            <p className="text-[10px] text-[#9aa4ad]">
                              {owner.email || "-"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td className="px-3 text-[10px] text-[#354858]">
                        {owner.phone_number || "-"}
                      </td>

                      {/* Email Verification */}
                      <td className="px-3">
                        <span
                          className={`rounded-full px-2 py-1 text-[10px] ${
                            owner.email_verified
                              ? "bg-[#d9f8e4] text-[#159447]"
                              : "bg-[#fff1c7] text-[#bd7b00]"
                          }`}
                        >
                          {owner.email_verified ? "Verified" : "Pending"}
                        </span>
                      </td>

                      {/* Created At */}
                      <td className="px-3 text-[10px] text-[#354858]">
                        {formatDate(owner.created_at)}
                      </td>

                      {/* Action */}
                      <td className="px-3">
                        <button
                          onClick={() => getOwnerProperties(owner)}
                          className="flex items-center gap-1.5 rounded-[5px] border border-[#dfe4e8] px-2.5 py-1.5 text-[10px] font-medium text-[#243846] transition hover:bg-[#f5f7f9]"
                        >
                          <Eye size={12} />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Owner Properties Modal */}
      {isPropertyModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closePropertyModal}
        >
          <div
            className="w-full max-w-[1000px] overflow-hidden rounded-[10px] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#edf0f2] px-5 py-4">
              <div>
                <h2 className="text-[15px] font-semibold text-[#142b3b]">
                  Owner Properties
                </h2>

                {selectedOwner && (
                  <p className="mt-1 text-[10px] text-[#84909b]">
                    {selectedOwner.first_name} {selectedOwner.last_name}
                    {selectedOwner.email ? ` • ${selectedOwner.email}` : ""}
                  </p>
                )}
              </div>

              <button
                onClick={closePropertyModal}
                className="flex h-7 w-7 items-center justify-center rounded-full text-[#71808c] transition hover:bg-[#f2f5f8]"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="max-h-[550px] overflow-y-auto p-5">
              {propertyLoading ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <p className="text-[11px] text-[#84909b]">
                    Loading properties...
                  </p>
                </div>
              ) : propertyError ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <p className="text-[11px] text-[#df3d3d]">{propertyError}</p>
                </div>
              ) : ownerProperties.length === 0 ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <p className="text-[11px] text-[#84909b]">
                    No properties found for this owner.
                  </p>
                </div>
              ) : (
                <>
                  {/* Property Summary */}
                  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">
                        Total Properties
                      </p>

                      <p className="mt-1 text-[13px] font-semibold text-[#243846]">
                        {ownerProperties.length}
                      </p>
                    </div>

                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">Approved</p>

                      <p className="mt-1 text-[13px] font-semibold text-[#159447]">
                        {
                          ownerProperties.filter(
                            (property) =>
                              getSubmissionStatus(
                                property.submission_status,
                              ) === "Approved",
                          ).length
                        }
                      </p>
                    </div>

                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">Pending</p>

                      <p className="mt-1 text-[13px] font-semibold text-[#bd7b00]">
                        {
                          ownerProperties.filter(
                            (property) =>
                              getSubmissionStatus(
                                property.submission_status,
                              ) === "Pending",
                          ).length
                        }
                      </p>
                    </div>

                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">Total Funding</p>

                      <p className="mt-1 text-[13px] font-semibold text-[#243846]">
                        AED{" "}
                        {ownerProperties
                          .reduce(
                            (total, property) =>
                              total + Number(property.property_value_aed || 0),
                            0,
                          )
                          .toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Properties Table */}
                  <div className="overflow-x-auto rounded-[8px] border border-[#e0e4e8]">
                    <table className="w-full min-w-[1100px]">
                      <thead>
                        <tr className="border-b border-[#e7eaed] bg-[#fafbfc]">
                          {[
                            "Property",
                            "Type",
                            "Location",
                            "Value",
                            "Area",
                            "Tokens",
                            "Price / Token",
                            "Yield",
                            "Funding",
                            "Status",
                            "Published",
                          ].map((heading) => (
                            <th
                              key={heading}
                              className="px-3 py-2.5 text-left text-[10px] font-medium text-[#71808c]"
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {ownerProperties.map((property) => {
                          const submissionStatus = getSubmissionStatus(
                            property.submission_status,
                          );

                          return (
                            <tr
                              key={property.id}
                              className="border-b border-[#edf0f2] last:border-0"
                            >
                              {/* Property */}
                              <td className="px-3 py-3">
                                <div className="max-w-[180px]">
                                  <p className="truncate text-[10px] font-medium text-[#243846]">
                                    {property.name || "-"}
                                  </p>

                                  <p className="mt-0.5 text-[9px] text-[#9aa4ad]">
                                    ID: {property.id}
                                  </p>
                                </div>
                              </td>

                              {/* Type */}
                              <td className="px-3 text-[10px] text-[#354858]">
                                {property.property_type || "-"}
                              </td>

                              {/* Location */}
                              <td className="px-3 text-[10px] text-[#354858]">
                                <span
                                  className="block max-w-[140px] truncate"
                                  title={property.location}
                                >
                                  {property.location || "-"}
                                </span>
                              </td>

                              {/* Value */}
                              <td className="px-3 text-[10px] font-medium text-[#263a48]">
                                AED{" "}
                                {Number(
                                  property.property_value_aed || 0,
                                ).toLocaleString()}
                              </td>

                              {/* Area */}
                              <td className="px-3 text-[10px] text-[#354858]">
                                {Number(
                                  property.area_sqft || 0,
                                ).toLocaleString()}{" "}
                                sqft
                              </td>

                              {/* Blocks */}
                              <td className="px-3 text-[10px] text-[#354858]">
                                <div>
                                  <p>
                                    {property.blocks_sold ?? 0} /{" "}
                                    {property.total_tokens ?? 0}
                                  </p>

                                  <p className="text-[9px] text-[#9aa4ad]">
                                    sold
                                  </p>
                                </div>
                              </td>

                              {/* Price / Block */}
                              <td className="px-3 text-[10px] font-medium text-[#263a48]">
                                AED{" "}
                                {Number(
                                  property.price_per_block_aed || 0,
                                ).toLocaleString()}
                              </td>

                              {/* Yield */}
                              <td className="px-3 text-[10px] font-medium text-[#159447]">
                                {property.gross_annual_yield_pct ?? 0}%
                              </td>

                              {/* Funding */}
                              <td className="px-3 text-[10px] font-medium text-[#263a48]">
                                {property.funding_pct ?? 0}%
                              </td>

                              {/* Submission Status */}
                              <td className="px-3">
                                <span
                                  className={`rounded-full px-2 py-1 text-[10px] ${
                                    submissionStatus === "Approved"
                                      ? "bg-[#d9f8e4] text-[#159447]"
                                      : submissionStatus === "Rejected"
                                        ? "bg-[#ffe0e0] text-[#df3d3d]"
                                        : "bg-[#fff1c7] text-[#bd7b00]"
                                  }`}
                                >
                                  {submissionStatus}
                                </span>
                              </td>

                              {/* Published */}
                              <td className="px-3">
                                <span
                                  className={`text-[10px] ${
                                    property.is_published
                                      ? "text-[#159447]"
                                      : "text-[#9aa4ad]"
                                  }`}
                                >
                                  ●{" "}
                                  {property.is_published
                                    ? "Published"
                                    : "Unpublished"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end border-t border-[#edf0f2] px-5 py-3">
              <button
                onClick={closePropertyModal}
                className="rounded-[6px] bg-[#142b3b] px-4 py-2 text-[10px] font-medium text-white transition hover:bg-[#1d4055]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PropertyOwners;
